/*
 * Dehde PDP v2 — small helpers for the dehde_* blocks (loaded only on templates/product.dehde-v2.json).
 *
 * Variant sync: the theme's <product-variants> listens to `change`, updates the hidden input[name="id"] of the
 * add-to-cart form synchronously, then re-renders price/stock via fetch. We listen to the same bubbling `change`
 * on document (fires AFTER the theme's own listener), read the resolved variant id from the real ATC form and
 * update: Klarna instalment, sticky bar (image / variant / price / disabled state), quote-form product field,
 * WhatsApp prefilled message. No duplication of the theme's variant logic.
 *
 * Sticky bar: IntersectionObserver on the main ATC button; the bar shows only once that button is ABOVE the
 * viewport. Its button calls requestSubmit() on the real form, so atc-submit-form (quantity, cart drawer,
 * error handling) runs exactly as for the main button.
 */
(() => {
  // money_format may contain HTML (e.g. <span class=money>); DOMParser never executes it.
  const stripTags = (html) => new DOMParser().parseFromString(String(html), 'text/html').body.textContent;
  const money = (cents) => {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return stripTags(Shopify.formatMoney(cents, window.money_format));
    }
    return (cents / 100).toFixed(2);
  };

  function initSection(container) {
    const sectionId = container.dataset.productContainer;
    const jsonEl = container.querySelector('script[type="application/json"][id^="ProductCardJson-"]');
    const atcForm = container.querySelector('atc-submit-form form[data-type="add-to-cart-form"]');
    if (!jsonEl || !atcForm) return;

    let product;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }

    const idInput = atcForm.querySelector('input[name="id"]');
    const mainBtn = atcForm.querySelector('[type="submit"][name="add"]');
    const sticky = document.querySelector(`[data-dehde-sticky-atc="${sectionId}"]`);
    const klarna = container.querySelector('[data-dehde-klarna]');
    const klarnaRow = container.querySelector('[data-dehde-klarna-row]');
    const variantsEl = container.querySelector('product-variants');
    const contract = container.querySelector('[data-dehde-contract]');
    const quoteModal = document.querySelector(`[data-modal="modal-dehde-quote-${sectionId}"]`);

    const currentVariant = () => product.variants.find((v) => String(v.id) === String(idInput && idInput.value));

    function productLabel(variant) {
      const single = product.variants.length === 1 && variant && variant.title === 'Default Title';
      return variant && !single ? `${product.title} – ${variant.title}` : product.title;
    }

    // Options currently checked in the swatches. For an unavailable combination the theme keeps the PREVIOUS
    // variant id in the form, so we compare against the checked options to know the combo doesn't exist.
    function checkedOptions() {
      if (!variantsEl) return null;
      return Array.from(variantsEl.querySelectorAll('fieldset')).map((fs) => {
        const r = fs.querySelector('input[type="radio"]:checked');
        return r ? r.value : null;
      });
    }

    function sync() {
      const variant = currentVariant();
      if (!variant) return;
      const opts = checkedOptions();
      const comboMissing = !!(opts && opts.length > 1 && opts.join(' / ') !== variant.options.join(' / '));
      // The theme disables the main button for sold-out/unavailable — mirror its state everywhere.
      const unavailable = comboMissing || (mainBtn ? mainBtn.disabled : !variant.available);

      if (klarna && klarnaRow) {
        const amount = klarna.querySelector('[data-dehde-klarna-amount]');
        if (amount) amount.textContent = money(Math.round(variant.price / 3));
        const min = klarnaRow.dataset.min ? Number(klarnaRow.dataset.min) : null;
        const max = klarnaRow.dataset.max ? Number(klarnaRow.dataset.max) : null;
        const inRange = (min === null || variant.price >= min) && (max === null || variant.price <= max);
        klarnaRow.classList.toggle('hidden', unavailable || !inRange);
      }

      if (sticky) {
        sticky.querySelectorAll('[data-dehde-sticky-price]').forEach((el) => {
          el.textContent = comboMissing ? '' : money(variant.price);
        });
        const variantEl = sticky.querySelector('[data-dehde-sticky-variant]');
        if (variantEl) variantEl.textContent = comboMissing ? opts.join(' / ') : variant.title;
        const img = sticky.querySelector('[data-dehde-sticky-image]');
        const media = variant.featured_media && variant.featured_media.preview_image;
        if (img && media && media.src) {
          const src = new URL(media.src, window.location.href);
          src.searchParams.set('width', '96');
          src.searchParams.set('height', '96');
          src.searchParams.set('crop', 'center');
          img.src = src.toString();
          img.removeAttribute('srcset');
        }
        const btn = sticky.querySelector('[data-dehde-sticky-btn]');
        if (btn && mainBtn) {
          btn.disabled = unavailable;
          btn.textContent = comboMissing && window.variantStrings ? window.variantStrings.unavailable : mainBtn.textContent.trim();
        }
      }

      const label = productLabel(variant);
      if (contract) {
        const tpl = contract.dataset.waTemplate || '';
        if (contract.dataset.waNumber) {
          // every wa.me link in the box: the WhatsApp button and the consultant's number (dehde-wa-button, dehde-consultant)
          contract.querySelectorAll('a[href*="wa.me/"]').forEach((wa) => {
            wa.href = `https://wa.me/${contract.dataset.waNumber}?text=${encodeURIComponent(tpl.replace('{prodotto}', label))}`;
          });
        }
      }
      if (quoteModal) {
        const input = quoteModal.querySelector('[data-dehde-quote-product-input]');
        if (input) input.value = label;
        const urlInput = quoteModal.querySelector('[data-dehde-quote-url-input]');
        if (urlInput) urlInput.value = `${window.location.origin}${window.location.pathname}?variant=${variant.id}`;
        const shown = quoteModal.querySelector('[data-dehde-quote-product]');
        if (shown) shown.textContent = label;
      }
    }

    // Theme listener is on <product-variants>; ours on document runs after it (bubbling order).
    document.addEventListener('change', (event) => {
      if (event.target.closest && event.target.closest('product-variants') && container.contains(event.target)) {
        sync();
        // The theme may toggle the button state again after its fetch — re-sync once it settles.
        setTimeout(sync, 600);
      }
    });

    // Quote form: after Shopify's POST reload, reopen the modal so the success/error message is visible.
    if (quoteModal && quoteModal.querySelector('[data-dehde-quote-autoopen]')) {
      const opener = container.querySelector('[data-modal-ref^="modal-dehde-quote-"][data-expanded="false"]');
      if (opener) window.requestAnimationFrame(() => opener.click());
    }

    if (sticky && mainBtn) initSticky(sticky, mainBtn, atcForm);
    sync();
  }

  function initSticky(sticky, mainBtn, atcForm) {
    sticky.hidden = false;
    const btn = sticky.querySelector('[data-dehde-sticky-btn]');
    if (btn) {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        if (typeof atcForm.requestSubmit === 'function') atcForm.requestSubmit();
        else atcForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }
    if (!('IntersectionObserver' in window)) return;
    const setVisible = (show) => {
      if (show) document.documentElement.style.setProperty('--dehde-sticky-atc-height', `${sticky.offsetHeight}px`);
      document.body.classList.toggle('dehde-sticky-atc-visible', show);
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      sticky.inert = !show;
    };
    // Phones (< 768px): bar visible whenever the main ATC is off-screen in EITHER direction, so name + price + CTA
    //   are reachable from the first screen.
    // Desktop: bar only after the main ATC has scrolled out ABOVE the viewport. The root is extended far below the
    //   viewport so a below-fold button still counts as "intersecting" (a plain observer would miss fast jumps
    //   from below-fold to above-fold, both being "not intersecting").
    // Phones also require the user to have scrolled > 80px, so the first paint (brand, name, chips, price, Klarna)
    // is never covered; the bar slides in as soon as scrolling starts. Only scrollY is read (no layout work).
    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const SCROLL_THRESHOLD = 80;
    let observer = null;
    let atcOut = false;
    const update = () => {
      // body.dehde-scrolled also reveals the theme's floating socials widget on phones (see dehde-pdp.css)
      document.body.classList.toggle('dehde-scrolled', window.scrollY > SCROLL_THRESHOLD);
      const scrolledEnough = !mobileQuery.matches || window.scrollY > SCROLL_THRESHOLD;
      setVisible(atcOut && scrolledEnough);
    };
    const observe = () => {
      if (observer) observer.disconnect();
      const rootMargin = mobileQuery.matches ? '0px' : '0px 0px 100000px 0px';
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { atcOut = !entry.isIntersecting; update(); });
      }, { rootMargin });
      observer.observe(mainBtn);
    };
    observe();
    window.addEventListener('scroll', update, { passive: true });
    update();
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', observe);
  }

  function init() {
    document.querySelectorAll('[data-product-container]').forEach((container) => {
      if (container.dataset.dehdeInit) return;
      container.dataset.dehdeInit = 'true';
      initSection(container);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
